import React, { memo, useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import RetroLoadingGames from './RetroLoadingGames';
import RetroLoadingDots from './RetroLoadingDots';
import ImageCard from './ImageCard';
import ImageCardSmall from './ImageCardSmall';
import ImageGridControls from './ImageGridControls';
import RetroFreeDrawing from './RetroFreeDrawing';
import RetroPixelDrawer from './RetroPixelDrawer';
import RetroPixelChallenge from './RetroPixelChallenge';
import ImageCarousel from './ImageCarousel';
import { getRandomActiveGame } from '../utils/gameSelector';

const CreateImageGridWithGames = ({ 
  images = [], 
  isLoading = false,
  favorites = new Set(),
  onToggleFavorite = () => {},
  onDelete = () => {},
  newImageIds = new Set(),
  generationProgress = {},
  onImageClick = () => {},
  onFavorite = () => {},
  onImageLoaded = () => {},
  onRegenerate = () => {},
  resetRotationKey = 0,
  isGroupedByPrompt = false,
  setIsGroupedByPrompt = () => {},
  sortOrder = 'newest',
  showFavoriteCount = false,
  isHighlightingLikes = false,
  isHighlightingPublic = false,
  onLoadMore = () => {},
  hasMore = false,
  loadingMore = false,
  onCopyToPrompt = () => {},
  showFavoritesOnly = false,
  setShowFavoritesOnly = () => {},
  setIsHighlightingLikes = () => {},
  setSortOrder = () => {},
  isAuthenticated = false,
  showPublicOnly = false,
  setShowPublicOnly = () => {},
  setIsHighlightingPublic = () => {},
  userId = null,
  showPublicToggle = false,
  showGrouping = false,
  onFullscreenClick = () => {},
  showFullscreen = false,
  isCompactGrid = false,
  setIsCompactGrid = () => {}
}) => {
  // Component rendered successfully
  // Track active games and their states
  const [activeGames, setActiveGames] = useState(new Set());
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [gameResults, setGameResults] = useState({});
  const [isShowingPopularLikes, setIsShowingPopularLikes] = useState(false);
  const [drawings, setDrawings] = useState(new Map());
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [localCounts, setLocalCounts] = useState({});
  // Track which generating cards have been clicked to start games
  const [clickedGeneratingCards, setClickedGeneratingCards] = useState(new Set());
  // Cache selected games for each placeholder to prevent re-randomization on hover
  const [selectedGames, setSelectedGames] = useState(new Map());

  // Convert favorites to Set if it's an array
  const favoritesSet = useMemo(() => {
    return favorites instanceof Set ? favorites : new Set(favorites);
  }, [favorites]);

  // Track active games state changes
  useEffect(() => {
    // Active games state management
  }, [activeGames, loadedImages]);

  // Effect to sync hover state with highlighting
  useEffect(() => {
    if (sortOrder === 'popular') {
      setIsHighlightingLikes?.(isShowingPopularLikes);
    }
  }, [isShowingPopularLikes, sortOrder, setIsHighlightingLikes]);

  // Effect to start games when new images are being generated
  useEffect(() => {
    if (newImageIds?.size && isLoading) {
      setActiveGames(new Set(Array.from(newImageIds)));
      setDrawings(new Map());
    }
  }, [newImageIds, isLoading]);

  // Initialize local counts from images
  useEffect(() => {
    const counts = {};
    images.forEach(image => {
      counts[image.id] = image.favoriteCount || 0;
    });
    setLocalCounts(counts);
  }, [images]);

  const handleImageLoad = (imageId) => {
    const actualId = imageId.startsWith('placeholder-') ? imageId.split('placeholder-')[1] : imageId;
    setLoadedImages(prev => {
      const next = new Set(prev);
      next.add(actualId);
      return next;
    });
  };

  const handleGameComplete = (imageId, results) => {
    const actualId = imageId.startsWith('placeholder-') ? imageId.split('placeholder-')[1] : imageId;
    setGameResults(prev => ({
      ...prev,
      [actualId]: results
    }));
  };

  const handleRevealClick = (imageId) => {
    const actualId = imageId.startsWith('placeholder-') ? imageId.split('placeholder-')[1] : imageId;
    setActiveGames(prev => {
      const next = new Set(prev);
      next.delete(actualId);
      return next;
    });
  };

  const renderLoadingState = () => {
    if (!newImageIds?.size) return null;

    const firstNewId = Array.from(newImageIds)[0];
    const generatedImage = images.find(img => img.id === firstNewId);
    const imageUrl = generatedImage?.url || generatedImage?.s3Url;

    return (
      <RetroLoadingGames
        loadedImages={Array.from(loadedImages)}
        totalExpected={newImageIds.size}
        generatedImageUrl={imageUrl}
      />
    );
  };

  // Group images by prompt if needed
  const groupedImages = useMemo(() => {
    if (!isGroupedByPrompt) return { '': images };
    
    return images.reduce((groups, image) => {
      // Use basePrompt for grouping instead of the full prompt
      const prompt = image.basePrompt || '';
      if (!groups[prompt]) groups[prompt] = [];
      groups[prompt].push(image);
      return groups;
    }, {});
  }, [images, isGroupedByPrompt]);

  const handleLocalImageClick = useCallback((image) => {
    if (!image.id.startsWith('placeholder-')) {
      const index = images.findIndex(img => img.id === image.id);
      setSelectedImageIndex(index);
      // Call the parent click handler as well
      onImageClick?.(image);
    }
  }, [images, onImageClick]);

  const handleCarouselClose = useCallback(() => {
    setSelectedImageIndex(null);
  }, []);

  const handleCarouselNext = useCallback(() => {
    setSelectedImageIndex(prev => (prev + 1) % images.length);
  }, [images.length]);

  const handleCarouselPrev = useCallback(() => {
    setSelectedImageIndex(prev => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  const handleGeneratingCardClick = useCallback((imageId) => {
    setClickedGeneratingCards(prev => new Set(prev).add(imageId));
  }, []);

  // Cleanup cached games when generation completes
  useEffect(() => {
    Object.entries(generationProgress).forEach(([genId, progress]) => {
      if (progress?.status === 'complete') {
        const placeholderId = `placeholder-${genId}`;
        setSelectedGames(prev => {
          const next = new Map(prev);
          next.delete(placeholderId);
          return next;
        });
        setClickedGeneratingCards(prev => {
          const next = new Set(prev);
          next.delete(placeholderId);
          return next;
        });
      }
    });
  }, [generationProgress]);

  // Initialize games for new placeholders
  useEffect(() => {
    const placeholders = images.filter(img => img.id.startsWith('placeholder-'));
    const needsGames = placeholders.filter(img => !selectedGames.has(img.id));
    
    if (needsGames.length > 0) {
      setSelectedGames(prev => {
        const next = new Map(prev);
        needsGames.forEach(img => {
          next.set(img.id, getRandomActiveGame());
        });
        return next;
      });
    }
  }, [images.length]); // Only re-run when number of images changes

  const handleLocalToggleFavorite = async (imageId) => {
    const actualId = imageId.startsWith('placeholder-') ? imageId.split('placeholder-')[1] : imageId;
    
    // Get current state
    const wasAlreadyFavorited = favoritesSet.has(actualId);
    const currentCount = localCounts[actualId] || 0;
    const newCount = wasAlreadyFavorited ? Math.max(0, currentCount - 1) : currentCount + 1;

    // Update local count immediately based on the PREVIOUS favorite state
    setLocalCounts(prev => {
      const updated = {
        ...prev,
        [actualId]: newCount
      };
      return updated;
    });

    try {
      // Call the parent handler
      await onToggleFavorite?.(actualId);
    } catch (error) {
      // Revert on error
      setLocalCounts(prev => ({
        ...prev,
        [actualId]: currentCount
      }));
    }
  };

  return (
    <div className="space-y-6">
      <ImageGridControls 
        showFavoritesOnly={showFavoritesOnly}
        setShowFavoritesOnly={setShowFavoritesOnly}
        showPublicOnly={showPublicOnly}
        setShowPublicOnly={setShowPublicOnly}
        isHighlightingLikes={isHighlightingLikes}
        setIsHighlightingLikes={setIsHighlightingLikes}
        isHighlightingPublic={isHighlightingPublic}
        setIsHighlightingPublic={setIsHighlightingPublic}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        isGroupedByPrompt={isGroupedByPrompt}
        setIsGroupedByPrompt={setIsGroupedByPrompt}
        onFullscreenClick={onFullscreenClick}
        isCompactGrid={isCompactGrid}
        setIsCompactGrid={setIsCompactGrid}
        isAuthenticated={isAuthenticated}
      />

      {isLoading ? (
        renderLoadingState()
      ) : (
        <motion.div layout className="space-y-8">
          {Object.entries(groupedImages).map(([prompt, groupImages], groupIndex) => {
            // Calculate starting index for this group based on previous groups
            const baseIndex = Object.entries(groupedImages)
              .slice(0, groupIndex)
              .reduce((sum, [_, prevImages]) => sum + prevImages.length, 0);

            return (
              <motion.div 
                key={prompt || 'ungrouped'}
                layout="position"
                className="space-y-4"
                onMouseEnter={() => sortOrder === 'popular' && setIsShowingPopularLikes(true)}
                onMouseLeave={() => setIsShowingPopularLikes(false)}
              >
                <motion.div 
                  layout="position"
                  className={(() => {
                    const gridClasses = `grid auto-rows-fr ${
                      isCompactGrid 
                        ? 'gap-6 grid-cols-4 sm:grid-cols-6 lg:grid-cols-8' 
                        : 'gap-12 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
                    }`;
                    console.log('[RETRO-GRID] 🔲 Grid classes applied:', {
                      isCompactGrid,
                      gridClasses,
                      timestamp: new Date().toISOString()
                    });
                    return gridClasses;
                  })()}
                  style={{ 
                    overflow: 'visible',
                    padding: isCompactGrid ? '12px' : '16px'
                  }}
                  key={`grid-${isCompactGrid ? 'compact' : 'normal'}`}
                >
                  {groupImages.map((image, index) => {
                    const isPlaceholder = image.id.startsWith('placeholder-');
                    const actualId = isPlaceholder ? image.id.split('placeholder-')[1] : image.id;
                    const isGameActive = activeGames.has(actualId);
                    
                    // Determine if we're still generating (should show generating animation)
                    const generationId = isPlaceholder ? actualId : image.generationId;
                    const isStillGenerating = generationId && generationProgress[generationId]?.status !== 'complete';
                    
                    // Check if this generating card has been clicked
                    const hasBeenClicked = clickedGeneratingCards.has(image.id);
                    
                    const globalIndex = baseIndex + index;



                    // Choose component based on compact mode
                    const CardComponent = isCompactGrid ? ImageCardSmall : ImageCard;
                    
                    return (
                      <CardComponent
                        key={image.id}
                        image={{
                          ...image,
                          favoriteCount: localCounts[actualId] || image.favoriteCount || 0,
                          animationDelay: index * 0.06
                        }}
                        onImageLoaded={() => handleImageLoad(image.id)}
                        isFavorited={favoritesSet.has(actualId)}
                        onToggleFavorite={() => {
                          handleLocalToggleFavorite(image.id);
                        }}
                        onClick={() => {
                          // If this is a generating placeholder that hasn't been clicked, start the game
                          if (isPlaceholder && isStillGenerating && !hasBeenClicked) {
                            // Always assign a new random game on click for immediate start
                            const newGame = getRandomActiveGame();
                            setSelectedGames(prev => new Map(prev).set(image.id, newGame));
                            handleGeneratingCardClick(image.id);
                          } else {
                            handleLocalImageClick(image);
                          }
                        }}
                        isGenerating={isPlaceholder ? false : isStillGenerating}
                        // Only pass these props to the full ImageCard, not ImageCardSmall
                        {...(isCompactGrid ? {} : {
                          forceShowLikes: false,
                          showFavoriteCount: showFavoriteCount,
                          onDelete: onDelete,
                          onFavorite: onFavorite,
                          onRegenerate: onRegenerate,
                          isHighlightingLikes: isHighlightingLikes,
                          isHighlightingPublic: isHighlightingPublic,
                          onCopyToPrompt: onCopyToPrompt,
                          isNew: newImageIds.has(actualId),
                          hideHoverButtons: false
                        })}
                        renderOverlay={(isPlaceholder && isStillGenerating) || (isGameActive || hasBeenClicked) ? () => {
                          // If it's a placeholder that's generating but hasn't been clicked, show "Creating" animation
                          if (isPlaceholder && isStillGenerating && !hasBeenClicked) {
                            return (
                              <div className="absolute inset-0 bg-black rounded-xl flex items-center justify-center cursor-pointer">
                                <RetroLoadingDots 
                                  text="Creating"
                                  size="28px" 
                                  className="text-white" 
                                />
                              </div>
                            );
                          }
                          
                          // If clicked or game is active, show the game
                          if (isGameActive || hasBeenClicked) {
                            // Get the cached game for this placeholder
                            const selectedGame = selectedGames.get(image.id);
                            
                            // If no game is cached yet, fallback
                            if (!selectedGame) {
                              return null;
                            }
                            
                            const GameComponent = selectedGame.component;
                            
                                                      return (
                            <div className="absolute inset-0 bg-black rounded-xl overflow-hidden" style={{ zIndex: 10 }}>
                              <motion.div 
                                className="w-full h-full relative"
                                style={{ 
                                  maxWidth: '100%', 
                                  maxHeight: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <div 
                                  className="w-full h-full"
                                  style={{ 
                                    transform: 'scale(0.8)',
                                    transformOrigin: 'center',
                                    maxWidth: '100%',
                                    maxHeight: '100%'
                                  }}
                                >
                                  <GameComponent
                                    isActive={hasBeenClicked}
                                    showStartButton={false}
                                    generatedImage={image}
                                    onGameComplete={(results) => handleGameComplete(image.id, results)}
                                    skipSaveOnUnmount={true}
                                    componentId={`${selectedGame.id}-${image.id}`}
                                    isGenerating={selectedGame.id === 'drawing' ? false : isStillGenerating}
                                  />
                                </div>
                                {/* Reveal Button Overlay */}
                                <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                                  <motion.button
                                    onClick={() => handleRevealClick(image.id)}
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    className="bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg font-['VT323'] text-xl text-white pointer-events-auto"
                                  >
                                    Reveal Image
                                  </motion.button>
                                </div>
                              </motion.div>
                            </div>
                          );
                          }
                          
                          return null;
                        } : undefined}
                      />
                    );
                  })}
                </motion.div>
              </motion.div>
            );
          })}
        </motion.div>
      )}

      {hasMore && images.length > 0 && (
        <div className="flex justify-center pt-4">
          <motion.button
            onClick={onLoadMore}
            disabled={loadingMore}
            className="px-6 py-3 bg-black/[0.03] hover:bg-black/[0.05] rounded-lg font-['VT323'] text-[18px] text-black/60 hover:text-black/80 transition-colors relative overflow-hidden disabled:cursor-not-allowed"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {loadingMore ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full border-2 border-black/10 border-t-black/40 animate-spin" />
                Loading...
              </div>
            ) : (
              'Load More'
            )}
          </motion.button>
        </div>
      )}

      <AnimatePresence>
        {selectedImageIndex !== null && (
          <ImageCarousel
            images={images}
            currentIndex={selectedImageIndex}
            onClose={handleCarouselClose}
            onNext={handleCarouselNext}
            onPrev={handleCarouselPrev}
            onToggleFavorite={onToggleFavorite}
            onDelete={onDelete}
            favorites={favoritesSet}
            userId={userId}
            onCopyToPrompt={onCopyToPrompt}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default memo(CreateImageGridWithGames); 